import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import eventEvaluationApi from 'domain/eventEvaluation/api'
import LoaderSpinner from 'components/Shared/Loader/LoaderSpinner'

import TableMessage from 'components/Shared/TableMessage'
import { numberWithThousandsSeparators, snakeCaseToPascalCase } from 'utils/helpers'
import { ArrowUpRightIcon, LeftArrowIcon, RightArrowIcon } from 'images'
import { TransparentBgButton } from 'components/Shared/Button'
import FeedAdCard from 'components/Recommendations/EventCampaigns/Steps/Setup/Summary/PreviewAdsModal/AdCards/FeedAdCard'
import ReelsAdCard from 'components/Recommendations/EventCampaigns/Steps/Setup/Summary/PreviewAdsModal/AdCards/ReelsAdCard'
import DisplayAdCard from 'components/Recommendations/EventCampaigns/Steps/Setup/Summary/PreviewAdsModal/AdCards/GoogleDisplay'

// FeedAdCard/ReelsAdCard expect the campaign-setup creative shape — mapAdDetails
// already produces camelCase, so this just fills in the fields those cards
// additionally need. callToAction is rendered as-is by those cards (no
// translation of their own), so it has to arrive already resolved — same
// t([key, fallback]) pattern utils/helpers.js's getAdSuggestions uses before
// it reaches the same preview components.
function adaptMetaAd(details, t) {
  const rawCallToAction = details.callToAction
  return {
    body: details.body,
    headline: details.headline,
    callToAction: rawCallToAction && t([
      `Recommendations.campaign.setup.call_to_action.${rawCallToAction}`,
      snakeCaseToPascalCase(rawCallToAction),
    ]),
    destinationUrl: details.destinationUrl,
    media: details.media,
    carouselCards: details.carouselCards,
    pageName: details.pageName ?? 'Page Name',
  }
}

// GmailChromeCard renders headline/description/businessName/logo/media only
// — no CTA button (see its own header comment). description/businessName/logo
// aren't in the ad-details response yet — fall back gracefully until they are.
function adaptGoogleGmailAd(details) {
  return {
    skin: 'gmail-chrome',
    headline: details.headline,
    description: details.description ?? details.body,
    businessName: details.businessName ?? 'Business Name',
    logo: details.logo,
    media: details.media,
  }
}

// DemandGenCarouselCard (skin 'demand-gen-carousel') expects carouselCards —
// same {url, media, headline, ...} card shape our ad-details response uses.
function adaptGoogleCarouselAd(details) {
  return {
    skin: 'demand-gen-carousel',
    businessName: details.businessName ?? 'Business Name',
    logo: details.logo,
    body: details.description ?? details.body,
    carouselCards: details.carouselCards,
  }
}

function isCarouselAd(details) {
  return !details.media && Array.isArray(details.carouselCards) && details.carouselCards.length > 0
}

function isYoutubeAd(details) {
  return details.media?.type === 'video/youtube' || Boolean(details.media?.youtube_video_id)
}

// YoutubeChromeCard renders callToAction as-is (no translation of its own),
// same as the Meta cards — see adaptMetaAd's comment.
function adaptGoogleYoutubeAd(details, t) {
  const rawCallToAction = details.callToAction
  return {
    skin: 'youtube-chrome',
    headline: details.headline,
    description: details.description ?? details.body,
    businessName: details.businessName ?? 'Business Name',
    logo: details.logo,
    media: details.media,
    callToAction: rawCallToAction && t([
      `Recommendations.campaign.setup.call_to_action.${rawCallToAction}`,
      snakeCaseToPascalCase(rawCallToAction),
    ]),
  }
}

// Same isReel heuristic as BestPerformingAds.jsx's generatePlacementsForAd —
// there's no ad-type flag from the backend, only the creative's own aspect
// ratio, which is unavailable for a carousel ad (media is null there) so it
// defaults to the feed card.
function renderAdPreview(ad, details, t) {
  if (ad.platform === 'meta') {
    const { media } = details
    const isReel = media?.width && media?.height ? media.height / media.width > 1.4 : false
    const adapted = adaptMetaAd(details, t)

    if (isReel) {
      return (
        <div className="preview-ads-meta-card preview-ads-meta-card--reels">
          <ReelsAdCard ad={adapted} />
        </div>
      )
    }

    const feedSizeClass = isCarouselAd(details)
      ? 'preview-ads-meta-card--feed-mobile-carousel'
      : 'preview-ads-meta-card--feed-desktop'
    return (
      <div className={`preview-ads-meta-card ${feedSizeClass}`}>
        <FeedAdCard platform="facebook" ad={adapted} mobileView={true}/>
      </div>
    )
  }

  if (ad.platform === 'google') {
    let adapted
    if (isCarouselAd(details)) {
      adapted = adaptGoogleCarouselAd(details)
    } else if (isYoutubeAd(details)) {
      adapted = adaptGoogleYoutubeAd(details, t)
    } else {
      adapted = adaptGoogleGmailAd(details)
    }
    return (
      <div className="preview-ads-google-card">
        <DisplayAdCard ad={adapted} />
      </div>
    )
  }

  return details.media?.url && (
    <img className="ad-details-modal_media" src={details.media.url} alt={details.media.filename || details.name} />
  )
}

export default function AdDetailsModal({
  ad,
  pageItems,
  page,
  pageSize,
  eventId,
  platform,
  sortBy,
  sortOrder,
  currentIndex,
  total,
  onAdChange,
}) {
  const { t } = useTranslation()
  const [details, setDetails] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  // Flips true after the first successful details fetch and stays true —
  // distinguishes "nothing to show yet" (full-page spinner) from "showing
  // stale content while a next/prev fetch is in flight" (dimmed overlay).
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)

  // Own prev/next state, seeded from the table's current page/ad so stepping
  // within it is instant — crossing to a page the table hasn't loaded fires
  // this modal's own request instead of touching the table's page/data, so
  // the table never visibly changes page while this is open.
  const [activeAd, setActiveAd] = useState(ad)
  const [activeIndex, setActiveIndex] = useState(currentIndex)
  const [navLoading, setNavLoading] = useState(false)
  const [navError, setNavError] = useState(null)
  const pageCacheRef = useRef(null)
  if (pageCacheRef.current === null) {
    pageCacheRef.current = { [page]: pageItems }
  }

  useEffect(() => {
    onAdChange?.(activeAd)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAd])

  const goToIndex = async (targetIndex) => {
    if (targetIndex < 0 || targetIndex >= total) return
    const targetPage = Math.floor(targetIndex / pageSize) + 1
    const targetLocalIndex = targetIndex % pageSize
    const cachedItems = pageCacheRef.current[targetPage]

    if (cachedItems) {
      setActiveIndex(targetIndex)
      setActiveAd(cachedItems[targetLocalIndex] ?? null)
      return
    }

    setNavLoading(true)
    setNavError(null)
    const res = await eventEvaluationApi.fetchInsightsBreakdownAsync(eventId, 'ads', {
      platform,
      page: targetPage,
      limit: pageSize,
      sortBy,
      sortOrder,
    })
    setNavLoading(false)
    if (res.error) {
      setNavError(t('Dashboard.adDetails.error') || 'Failed to load ad details')
      return
    }
    pageCacheRef.current[targetPage] = res.data.items
    setActiveIndex(targetIndex)
    setActiveAd(res.data.items[targetLocalIndex] ?? null)
  }

  useEffect(() => {
    if (!activeAd?.id) return undefined
    let isMounted = true
    // Deliberately not resetting `details` to null here — keeps the
    // previous ad's content on screen (under the nav overlay below) instead
    // of flashing to the full-page spinner on every next/prev step.
    setLoading(true)
    setError(null)
    eventEvaluationApi.fetchAdDetailsAsync(eventId, activeAd.id, activeAd.platform).then((res) => {
      if (!isMounted) return
      if (res.error) {
        setError(t('Dashboard.adDetails.error') || 'Failed to load ad details')
        return
      }
      setDetails(res.data)
      setHasLoadedOnce(true)
    }).finally(() => {
      if (isMounted) setLoading(false)
    })
    return () => {
      isMounted = false
    }
  }, [activeAd?.id, eventId, t])

  // activeAd is only null before the very first ad is picked, and
  // hasLoadedOnce only flips once the first details fetch has ever
  // succeeded — both only true on the very first open, so the full-page
  // spinner only shows then. Any navLoading/loading after that keeps the
  // previous activeAd/details on screen under a dimmed overlay instead of
  // blanking the whole modal (see the nav overlay below).
  if (!activeAd || (loading && !hasLoadedOnce)) {
    return (
      <div className="ad-details-modal">
        <div className="mt-auto mb-auto">
          <LoaderSpinner text={t('common.loading')} className="m-4 p-2" />
        </div>
      </div>
    )
  }

  if (navError && !details) {
    return (
      <div className="ad-details-modal">
        <div className="mt-auto mb-auto">
          <TableMessage
            variant="error"
            title={t('Dashboard.adDetails.error.title')}
            description={t('Dashboard.adDetails.error.description')}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="ad-details-modal">
      {(navLoading || loading) && (
        <div className="ad-details-modal_nav-overlay">
          <LoaderSpinner text={t('common.loading')} />
        </div>
      )}

      {error && !details && (
        <div className='mt-auto mb-auto'>
            <TableMessage
            variant="error"
            title={t('Dashboard.adDetails.error.title')}
            description={t('Dashboard.adDetails.error.description')}
            />
        </div>
      )}

      {details && (
        <>
            <div className="ad-details-modal_content">
                <div className="media-container">
                    {renderAdPreview(activeAd, details, t)}

                    <p className="media-container_preview-warning">{t('Dashboard.insightsBreakdown.adDetailsModal.previewWarning')}</p>
                </div>

                <div className="ad-info">
                    <div className="ad-info_header">
                        <div className="ad-info_header_items">
                            {['impressions', 'clicks', 'reach', 'ctr'].map((item) => (
                                <div className="ad-info_header_item" key={item}>
                                    <p className="ad-info_header_item_value">
                                        {item === 'ctr'
                                            ? numberWithThousandsSeparators(activeAd[item], { maxFractionDigits: 2, suffix: '%' })
                                            : numberWithThousandsSeparators(activeAd[item])}
                                    </p>

                                    <p className="ad-info_header_item_label">{t(`Dashboard.insightsBreakdown.table.${item}`)}</p>
                                </div>
                            ))}
                        </div>

                        <p className="ad-info_header_spend">{t('Dashboard.insightsBreakdown.adDetailsModal.spend', { spend: numberWithThousandsSeparators(activeAd.spend) })}</p>
                    </div>

                    <dl className="ad-info_body">
                        {[
                            { key: 'headline', field: 'headline' },
                            { key: 'body', field: 'body' },
                            { key: 'description', field: 'description' },
                            { key: 'call_to_action', field: 'callToAction' },
                        ].filter(({ field }) => details[field]).map(({ key, field }) => (
                            <div className="ad-info_body_item" key={key}>
                                <p className="ad-info_body_item_label">{t(`Dashboard.insightsBreakdown.adDetailsModal.${key}`)}</p>

                                <dd className="ad-info_body_item_value">
                                    {field === 'callToAction'
                                        ? t([
                                            `Recommendations.campaign.setup.call_to_action.${details[field]}`,
                                            snakeCaseToPascalCase(details[field]),
                                        ])
                                        : details[field]}
                                </dd>
                            </div>
                        ))}

                        {details.destinationUrl && (
                            <div className="ad-info_body_item">
                                <dt className="ad-info_body_item_label">{t('Dashboard.insightsBreakdown.adDetailsModal.destination_url')}</dt>

                                <dd className="ad-info_body_item_value">
                                    <a
                                        className="ad-info_body_item_link"
                                        href={details.destinationUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        >
                                        {details.destinationUrl}

                                        <ArrowUpRightIcon width={16} height={16} />
                                    </a>
                                </dd>
                            </div>
                        )}
                    </dl>
                </div>
            </div>

            
            <div className="ad-details_footer">
                    <p className="ad-details_footer_text">
                        {t('Dashboard.insightsBreakdown.adDetailsModal.numberOfAds', { number: activeIndex + 1, total })}
                    </p>

                    <div className="ad-details_footer_actions">
                        <TransparentBgButton
                            onClick={() => goToIndex(activeIndex - 1)}
                            disabled={activeIndex <= 0 || loading || navLoading}
                            text={t('Dashboard.insightsBreakdown.adDetailsModal.previous')}
                            leftIcon={<LeftArrowIcon width={12} height={12} />}
                            color="navy"
                        />

                        <TransparentBgButton
                            onClick={() => goToIndex(activeIndex + 1)}
                            disabled={activeIndex >= total - 1 || loading || navLoading}
                            text={t('Dashboard.insightsBreakdown.adDetailsModal.next')}
                            rightIcon={<RightArrowIcon width={12} height={12} />}
                            color="navy"
                        />
                    </div>
            </div>
        </>
      )}
    </div>
  )
}

AdDetailsModal.propTypes = {
  ad: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    spend: PropTypes.number,
    clicks: PropTypes.number,
  }),
  pageItems: PropTypes.array,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  eventId: PropTypes.string.isRequired,
  platform: PropTypes.string,
  sortBy: PropTypes.string,
  sortOrder: PropTypes.string,
  currentIndex: PropTypes.number,
  total: PropTypes.number,
  onAdChange: PropTypes.func,
}
